/**
 * Drag the Words — Editor
 *
 * Custom FITB editor layout:
 *   - Shared "Wrong options" section at the top of the exercise detail
 *   - Each blank only shows accepted answers (no per-blank distractors)
 */

import * as React from 'react';
import {
    Badge,
    cn,
    createEmptyDocument,
    Label,
    Input,
    RichTextEditor,
    Textarea,
} from '@ai-content-hub/content-kit';
import {
    type EditTabDescriptor,
    type CompositeQuestionsSidebarRow,
    generateId,
    type ContentEditProps,
    CompositeEditShell,
    CompositeEditContentTab,
    CompositeEditSortableQuestionsLayout,
    orderItemsByQuestionsSidebarRows,
    persistQuestionsSidebarRows,
    reconcileQuestionsSidebarRows,
    resolvedQuestionsSidebarRows,
    useLocale,
} from '@ai-content-hub/content-core';
import {
    PageEdit,
    defaultPageData,
    defaultFITBConfig,
    defaultFITBElementData,
    normalizeFITBData,
    countBlanksInTiptap,
    type FITBElementData,
    type FITBConfig,
    type FITBBlank,
    type PageElementData,
} from '@ai-content-hub/content-elements';
import type { BlockContentType } from '@ai-content-hub/content-elements';
import type { QtDragTheWordsConfig, QtDragTheWordsData, QtDragTheWordsItem } from './schema';
import { createQtDragTheWordsItem, defaultQtDragTheWordsData } from './schema';
import { FileText, HelpCircle, Settings } from 'lucide-react';
import map from 'lodash/map';
import filter from 'lodash/filter';
import trim from 'lodash/trim';

const PAGE_BLOCK_TYPES: BlockContentType[] = [
    'rich-text',
    'image',
    'audio',
    'video',
    'text',
    'office-document',
];

/* ------------------------------------------------------------------ */
/* Semicolon-list helpers                                             */
/* ------------------------------------------------------------------ */

function parseListInput(raw: string): string[] {
    return raw.split(/[;\r\n]+/);
}

function parseListInputFinal(raw: string): string[] {
    return map(raw.split(/[;\r\n]+/), (s) => trim(s));
}

function listToInput(list: string[] | undefined): string {
    return (list ?? []).join('; ');
}

/* ------------------------------------------------------------------ */
/* Semicolon-list input with local state                              */
/* ------------------------------------------------------------------ */

interface BlankListInputProps {
    id?: string;
    value: string[] | undefined;
    onChange: (list: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
}

const BlankListInput: React.FC<BlankListInputProps> = ({
    id,
    value,
    onChange,
    placeholder,
    disabled,
    className,
    ...ariaProps
}) => {
    const externalText = listToInput(value);
    const [draft, setDraft] = React.useState(externalText);
    const [focused, setFocused] = React.useState(false);

    React.useEffect(() => {
        if (!focused) {
            setDraft(externalText);
        }
    }, [externalText, focused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setDraft(raw);
        onChange(parseListInput(raw));
    };

    const handleBlur = () => {
        setFocused(false);
        const cleaned = parseListInputFinal(draft);
        onChange(cleaned);
        setDraft(listToInput(cleaned));
    };

    return (
        <Input
            id={id}
            value={draft}
            onChange={handleChange}
            onFocus={() => setFocused(true)}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={className}
            aria-invalid={ariaProps['aria-invalid']}
            aria-describedby={ariaProps['aria-describedby']}
        />
    );
};

/* ------------------------------------------------------------------ */
/* Custom FITB editor for drag-the-words                              */
/* Shows: passage → shared wrong options → blanks (accepted only)     */
/* ------------------------------------------------------------------ */

interface DragTheWordsFITBEditorProps {
    value: FITBElementData;
    onChange: (value: FITBElementData) => void;
    sharedDistractors: string[];
    onSharedDistractorsChange: (distractors: string[]) => void;
    disabled?: boolean;
    t: (key: string, fallback?: string) => string;
}

const DragTheWordsFITBEditor: React.FC<DragTheWordsFITBEditorProps> = ({
    value,
    onChange,
    sharedDistractors,
    onSharedDistractorsChange,
    disabled = false,
    t,
}) => {
    const safeData = React.useMemo(
        () => normalizeFITBData({ ...defaultFITBElementData, ...value }),
        [value],
    );

    const handlePassageChange = (passage: FITBElementData['passage']) => {
        onChange(normalizeFITBData({ ...safeData, passage }));
    };

    const updateBlank = (index: number, patch: Partial<FITBBlank>) => {
        const blanks = map(safeData.blanks, (b, i) => (i === index ? { ...b, ...patch } : b));
        onChange({ ...safeData, blanks });
    };

    const blankCount = countBlanksInTiptap(safeData.passage);

    return (
        <div className="dtw-fitb-edit flex flex-col gap-4">
            {/* Passage editor */}
            <div className="dtw-fitb-edit-passage rounded-xl border-0 bg-card p-4 shadow-sm space-y-2">
                <Label className="text-base font-semibold">
                    {t('passageLabelFITB', 'Passage')}
                </Label>
                <p className="text-sm text-muted-foreground">
                    {t('passageFITBHint', 'Use three or more underscores (___) for each blank.')}
                </p>
                <RichTextEditor
                    value={safeData.passage}
                    onChange={handlePassageChange}
                    readOnly={disabled}
                    minHeight={220}
                    className="dtw-fitb-edit-passage-editor"
                    translate={t}
                />
            </div>

            {/* Shared Wrong Options — at the top of the blanks section */}
            <div className="dtw-fitb-edit-shared-distractors rounded-sm bg-muted/30 p-3 border border-border space-y-2">
                <Label htmlFor="dtw-shared-distractors">
                    {t('sharedDistractorsLabel', 'Wrong options')}
                </Label>
                <BlankListInput
                    id="dtw-shared-distractors"
                    value={sharedDistractors}
                    onChange={onSharedDistractorsChange}
                    placeholder={t('sharedDistractorsPlaceholder', 'e.g. wrong1; wrong2; wrong3')}
                    disabled={disabled}
                />
            </div>

            {/* Blanks — accepted answers only */}
            <div className="dtw-fitb-edit-blanks space-y-3">
                <h3 className="text-lg font-semibold">
                    {t('blanksHeading', 'Blanks')}
                </h3>
                {safeData.blanks.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">
                        {t('blanksEmptyFITB', 'Add ___ markers in the passage to create blanks.')}
                    </p>
                ) : (
                    <div className="space-y-3">
                        {map(safeData.blanks, (blank, index) => {
                            const hasAcceptedAnswer =
                                filter(blank.acceptedAnswers, (a) => trim(String(a)) !== '')
                                    .length > 0;
                            const acceptedError =
                                blankCount > 0 && !hasAcceptedAnswer
                                    ? 'Each blank requires at least one accepted answer'
                                    : undefined;
                            return (
                                <div
                                    key={blank.id}
                                    className="dtw-fitb-edit-blank-row rounded-sm bg-muted/30 p-3 border border-border space-y-3"
                                >
                                    <div className="flex items-center gap-2">
                                        <Badge
                                            variant="secondary"
                                            className="dtw-fitb-edit-blank-badge w-9 justify-center bg-info/10 text-info"
                                        >
                                            {index + 1}
                                        </Badge>
                                        <span className="text-sm text-muted-foreground">
                                            {t('blankNth', 'Blank')} {index + 1}
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor={`dtw-accept-${index}`}>
                                            {t('acceptedAnswers', 'Accepted answers')}
                                        </Label>
                                        <BlankListInput
                                            id={`dtw-accept-${index}`}
                                            value={blank.acceptedAnswers}
                                            onChange={(list) =>
                                                updateBlank(index, {
                                                    acceptedAnswers: list,
                                                })
                                            }
                                            placeholder={t(
                                                'correctAnswersSemicolonHint',
                                                'Correct answer(s); separate with semicolons',
                                            )}
                                            disabled={disabled}
                                            className={cn(
                                                acceptedError &&
                                                    'border-destructive ring-1 ring-destructive/40',
                                            )}
                                            aria-invalid={acceptedError ? true : undefined}
                                            aria-describedby={
                                                acceptedError
                                                    ? `dtw-blank-${index}-accepted-error`
                                                    : undefined
                                            }
                                        />
                                        {acceptedError ? (
                                            <p
                                                id={`dtw-blank-${index}-accepted-error`}
                                                role="alert"
                                                className="dtw-fitb-edit-blank-accepted-error text-sm text-destructive"
                                            >
                                                {acceptedError}
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Explanation */}
            <div className="dtw-fitb-edit-explanation space-y-2">
                <Label htmlFor="dtw-explanation">
                    {t('explanationAfterSubmit', 'Explanation (after submit)')}
                </Label>
                <Textarea
                    id="dtw-explanation"
                    value={safeData.explanation ?? ''}
                    onChange={(e) =>
                        onChange(normalizeFITBData({ ...safeData, explanation: e.target.value }))
                    }
                    placeholder={t('explainCorrectAnswer', 'Explain the correct answer...')}
                    rows={3}
                    disabled={disabled}
                    className="dtw-fitb-edit-explanation-input"
                />
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Main editor component                                              */
/* ------------------------------------------------------------------ */

export type DragTheWordsEditProps = ContentEditProps<QtDragTheWordsData> & {
    config?: QtDragTheWordsConfig;
    className?: string;
};

export const DragTheWordsEdit: React.FC<DragTheWordsEditProps> = ({
    value: data,
    onChange,
    disabled = false,
    className,
    configSlot,
    config,
    language,
}) => {
    const { t } = useLocale({ config: config?.i18n, initialLanguage: language });
    const [activeTab, setActiveTab] = React.useState<'content' | 'questions' | 'config'>('content');

    const safeData: QtDragTheWordsData = {
        ...defaultQtDragTheWordsData,
        ...data,
        pageContent: {
            ...defaultPageData,
            ...(data?.pageContent ?? {}),
            sections: data?.pageContent?.sections ?? defaultPageData.sections,
        },
        items: data?.items ?? [],
    };

    const items = safeData.items;
    const contentSectionCount = safeData.pageContent.sections?.length ?? 0;

    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [selectedSectionId, setSelectedSectionId] = React.useState<string | null>(null);

    // EAA-2083: only the exercise selection follows `items`; the section selection is left
    // alone. Every sidebar-rows change (typing a section title included) rebuilds `items`
    // as a new array, so with no exercises yet this effect cleared the selected section on
    // the first keystroke and closed its panel. A section that is really removed is
    // deselected by CompositeEditSortableQuestionsLayout itself.
    React.useEffect(() => {
        if (items.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((prev) => {
            if (prev && items.some((i) => i.id === prev)) return prev;
            return items[0].id;
        });
    }, [items]);

    const updateData = (updates: Partial<QtDragTheWordsData>) => {
        onChange({
            ...safeData,
            ...updates,
        });
    };

    const handlePageContentChange = (pageContent: PageElementData) => {
        updateData({ pageContent });
    };

    const addItem = () => {
        if (disabled) return;
        const newItem = createQtDragTheWordsItem(generateId());
        const nextItems = [...items, newItem];
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextItems, (i) => i.id))
            ),
        });
        setSelectedId(newItem.id);
        setSelectedSectionId(null);
        setActiveTab('questions');
    };

    const removeItem = (itemId: string) => {
        if (disabled) return;
        const nextItems = items.filter((i) => i.id !== itemId);
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(
                reconcileQuestionsSidebarRows(safeData.questionsSidebarRows, map(nextItems, (i) => i.id))
            ),
        });
    };

    const updateItem = (itemId: string, next: QtDragTheWordsItem) => {
        if (disabled) return;
        updateData({
            items: map(items, (it) => (it.id === itemId ? next : it)),
        });
    };

    const handleQuestionsSidebarRowsChange = (rows: CompositeQuestionsSidebarRow[]) => {
        if (disabled) return;
        const nextItems = orderItemsByQuestionsSidebarRows(items, rows);
        updateData({
            items: nextItems,
            questionsSidebarRows: persistQuestionsSidebarRows(rows),
        });
    };

    const handleAddSidebarSection = () => {
        if (disabled) return;
        const sid = generateId();
        const base = resolvedQuestionsSidebarRows(
            safeData.questionsSidebarRows,
            map(items, (i) => i.id)
        );
        handleQuestionsSidebarRowsChange([
            {
                kind: 'section',
                section: { id: sid, title: '', description: createEmptyDocument() },
            },
            ...base,
        ]);
        setSelectedSectionId(sid);
        setSelectedId(null);
    };

    const sidebarRows = React.useMemo(
        () => resolvedQuestionsSidebarRows(safeData.questionsSidebarRows, map(items, (i) => i.id)),
        [items, safeData.questionsSidebarRows]
    );

    const tabs: EditTabDescriptor[] = [
        { id: 'content', label: t('contentTab'), icon: FileText, badge: contentSectionCount },
        { id: 'questions', label: t('questionsTab'), icon: HelpCircle, badge: items.length },
        ...(configSlot ? [{ id: 'config', label: t('configTab'), icon: Settings }] : []),
    ];

    const addExerciseOptions = [
        {
            id: 'fitb-drag',
            label: 'Drag the Words',
        },
    ];

    return (
        <CompositeEditShell
            prefix="qt-drag-the-words-edit"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as 'content' | 'questions' | 'config')}
            configSlot={configSlot}
            className={cn('qt-drag-the-words-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'content') {
                    return (
                        <CompositeEditContentTab
                            prefix="qt-drag-the-words-edit"
                            disabled={disabled}
                            title={{
                                id: 'qt-dtw-title',
                                label: t('titleLabel', 'Title'),
                                value: safeData.title ?? '',
                                onChange: (v) => updateData({ title: v }),
                                placeholder: t('titlePlaceholder', 'Exercise title'),
                                required: true,
                            }}
                            titleInputClassName="qt-drag-the-words-edit-title-input"
                            description={{
                                id: 'qt-dtw-instructions',
                                label: t('instructionsLabel', 'Instructions (optional)'),
                                value: safeData.instructions ?? '',
                                onChange: (v) => updateData({ instructions: v }),
                                placeholder: t('instructionsPlaceholder', 'Learner-facing instructions'),
                                rows: 3,
                            }}
                            descriptionInputClassName="qt-drag-the-words-edit-instructions-input"
                            page={
                                <PageEdit
                                    value={safeData.pageContent}
                                    onChange={handlePageContentChange}
                                    disabled={disabled}
                                    hideSettings
                                    allowedBlockTypes={PAGE_BLOCK_TYPES}
                                    className="qt-drag-the-words-edit-page-inner"
                                />
                            }
                        />
                    );
                }
                if (tabId === 'questions') {
                    return (
                        <CompositeEditSortableQuestionsLayout
                            prefix="qt-drag-the-words-edit"
                            listTitle={t('exercisesListTitle', 'Exercises')}
                            emptyMessage={t('emptyExercisesMsg', 'No exercises yet. Add a drag-the-words passage.')}
                            items={items}
                            selectedId={selectedId}
                            onSelectId={setSelectedId}
                            selectedSectionId={selectedSectionId}
                            onSelectSectionId={setSelectedSectionId}
                            disabled={disabled}
                            minItemsToKeep={0}
                            sidebarRows={sidebarRows}
                            onSidebarRowsChange={handleQuestionsSidebarRowsChange}
                            onAddSidebarSection={handleAddSidebarSection}
                            addSidebarSectionLabel={t('addSection', 'Add section')}
                            removeSidebarSectionLabel={t('removeSection', 'Remove section')}
                            onRemoveItem={removeItem}
                            getItemLabel={(_itemId, index) =>
                                `${index + 1}. ${t('dragTheWordsBlanksPreview', 'Drag the words blanks')}`
                            }
                            addButtonLabel={t('addExercise', 'Add exercise')}
                            addButtonOptions={addExerciseOptions}
                            addButtonClassName="qt-drag-the-words-edit-add"
                            onAddItem={() => addItem()}
                            sectionDetailTitle={t('sectionDetailTitle', 'Section')}
                            detailTitle={t('exerciseDetailTitle', 'Exercise')}
                            detailBadge={
                                <Badge
                                    variant="outline"
                                    className="border-success/30 bg-success/10"
                                >
                                    {t('dragTheWordsBadge', 'Drag the Words')}
                                </Badge>
                            }
                            renderItemDetail={({ itemId }) => {
                                const rowItem = items.find((i) => i.id === itemId);
                                if (!rowItem) {
                                    return null;
                                }
                                return (
                                    <DragTheWordsFITBEditor
                                        value={rowItem.data}
                                        onChange={(d: FITBElementData) =>
                                            updateItem(rowItem.id, {
                                                ...rowItem,
                                                data: d,
                                            })
                                        }
                                        sharedDistractors={rowItem.sharedDistractors ?? []}
                                        onSharedDistractorsChange={(distractors) =>
                                            updateItem(rowItem.id, {
                                                ...rowItem,
                                                sharedDistractors: distractors,
                                            })
                                        }
                                        disabled={disabled}
                                        t={t}
                                    />
                                );
                            }}
                            detailContent={null}
                            detailEmptyMessage={t('detailEmptyMessage', 'Select an exercise, a section, or add one to start editing.')}
                            removeItemLabel={t('removeExercise', 'Remove exercise')}
                            richTextTranslate={t}
                        />
                    );
                }
                return null;
            }}
        />
    );
};

export default DragTheWordsEdit;
